<div class="cms-content-create-edit">
    <!-- Sticky Header -->
    <div class="sticky top-0 z-50 bg-white border-b border-gray-200 px-6 py-4 mb-6 shadow-sm">
        <div class="flex items-center justify-between mx-auto">
            <div class="flex items-center gap-4">
                <p-button icon="pi pi-arrow-left" text="true" (click)="goBack()" severity="secondary"></p-button>
                <div>
                    <h1 class="text-xl font-semibold text-gray-900">{{ isEditMode ? 'Edit Page' : 'Create New Page' }}</h1>
                    <p class="text-sm text-gray-500">{{ isEditMode ? 'Update existing content' : 'Add a new content page' }}</p>
                </div>
            </div>
            <div class="flex items-center gap-3">
                <p-button label="Cancel" (click)="goBack()" severity="secondary" styleClass="hidden sm:block"></p-button>
                <p-button label="Save Page" (click)="savePage()" icon="pi pi-save" severity="primary"></p-button>
            </div>
        </div>
    </div>

    <div class="pb-8">
        <form [formGroup]="contentForm" class="mx-auto space-y-6">
            <!-- Basic Settings Card -->
            <p-card styleClass="shadow-sm">
                <ng-template pTemplate="title">
                    <div class="flex items-center gap-2">
                        <i class="pi pi-cog text-blue-500"></i>
                        <span>Basic Settings</span>
                    </div>
                </ng-template>
                <ng-template pTemplate="subtitle">General page configuration</ng-template>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    <!-- Category -->
                    <div>
                        <label for="category" class="block mb-2 text-sm font-medium text-gray-700">Category *</label>
                        <p-select
                            id="category"
                            [options]="categories()"
                            formControlName="category"
                            optionLabel="name"
                            [showClear]="true"
                            placeholder="Select category"
                            styleClass="w-full"
                        ></p-select>
                        @if (contentForm.get('category')?.invalid && contentForm.get('category')?.touched) {
                            <small class="text-red-500 text-xs mt-1 block">Category is required</small>
                        }
                    </div>

                    <!-- From Port -->
                    <div>
                        <label class="block mb-2 text-sm font-medium text-gray-700">From Port</label>
                        <p-select
                            [disabled]="portLoading() || !ports().length"
                            [loading]="portLoading()"
                            [filterMatchMode]="'startsWith'"
                            formControlName="fromPortCode"
                            [options]="ports()"
                            optionLabel="code"
                            optionValue="code"
                            placeholder="Select port"
                            styleClass="w-full"
                            [appendTo]="'body'"
                            [filter]="true"
                            filterBy="code,explanation,city,country"
                            [virtualScroll]="true"
                            [virtualScrollItemSize]="34"
                            [showClear]="true"
                        ></p-select>
                    </div>

                    <!-- To Port -->
                    <div>
                        <label class="block mb-2 text-sm font-medium text-gray-700">To Port</label>
                        <p-select
                            [disabled]="portLoading() || !ports().length"
                            [loading]="portLoading()"
                            [filterMatchMode]="'startsWith'"
                            formControlName="toPortCode"
                            [options]="ports()"
                            optionLabel="code"
                            optionValue="code"
                            placeholder="Select port"
                            styleClass="w-full"
                            [appendTo]="'body'"
                            [filter]="true"
                            filterBy="code,explanation,city,country"
                            [virtualScroll]="true"
                            [virtualScrollItemSize]="34"
                            [showClear]="true"
                        ></p-select>
                    </div>

                    <!-- Sitemap Priority -->
                    <div>
                        <label for="sitemapPriority" class="block mb-2 text-sm font-medium text-gray-700">Sitemap Priority</label>
                        <p-inputnumber
                            inputId="sitemapPriority"
                            formControlName="sitemapPriority"
                            [min]="0"
                            [max]="1"
                            [minFractionDigits]="1"
                            [maxFractionDigits]="1"
                            [step]="0.1"
                            [showButtons]="true"
                            placeholder="0.8"
                            styleClass="w-full"
                            [ngClass]="{ 'ng-invalid ng-dirty': contentForm.get('sitemapPriority')?.invalid && contentForm.get('sitemapPriority')?.touched }"
                        ></p-inputnumber>
                        @if (contentForm.get('sitemapPriority')?.invalid && contentForm.get('sitemapPriority')?.touched) {
                            <small class="text-red-500 text-xs mt-1 block">Enter a value between 0.0 and 1.0</small>
                        } @else {
                            <small class="text-gray-400 text-xs mt-1 block">Leave empty to use the default (0.8).</small>
                        }
                    </div>
                </div>

                <!-- Scheduling -->
                <div class="mt-5 pt-5 border-t border-gray-100">
                    <div class="flex items-center justify-between mb-3">
                        <div class="flex items-center gap-2">
                            <i class="pi pi-calendar-clock text-gray-500"></i>
                            <span class="font-medium text-gray-700">Schedule Publication</span>
                        </div>
                        <p-toggleswitch
                            [ngModel]="isScheduled()"
                            (onChange)="toggleScheduledStatus($event.checked)"
                            [ngModelOptions]="{ standalone: true }"
                        ></p-toggleswitch>
                    </div>
                    @if (isScheduled()) {
                        <div class="bg-gray-50 rounded-lg p-4">
                            <label for="scheduledDate" class="block mb-2 text-sm font-medium text-gray-700"
                                >Schedule Date (UTC) - Current: {{ utcNow }}</label
                            >
                            <p-datepicker
                                dateFormat="dd.mm.yy"
                                [firstDayOfWeek]="1"
                                fluid
                                formControlName="scheduledDate"
                                inputId="scheduledDate"
                                [showTime]="true"
                                hourFormat="24"
                                styleClass="w-full md:w-80"
                            ></p-datepicker>
                            @if (contentForm.get('scheduledDate')?.invalid && contentForm.get('scheduledDate')?.touched) {
                                <small class="text-red-500 text-xs mt-1 block">Schedule Date is required</small>
                            }
                        </div>
                    }
                </div>
            </p-card>

            <!-- Images Card -->
            <p-card styleClass="shadow-sm">
                <ng-template pTemplate="title">
                    <div class="flex items-center gap-2">
                        <i class="pi pi-image text-purple-500"></i>
                        <span>Page Images</span>
                    </div>
                </ng-template>
                <ng-template pTemplate="subtitle">OG Image and Search Background</ng-template>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- OG Image -->
                    <div>
                        <label class="block mb-3 text-sm font-medium text-gray-700">OG Image (Max 5MB)</label>
                        <div class="border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                            @if (!ogImageFileName()) {
                                <div class="text-center">
                                    @if (isEditMode) {
                                        <input
                                            type="file"
                                            accept="image/*"
                                            (change)="uploadOgImage($event)"
                                            [disabled]="uploadingOgImage()"
                                            hidden
                                            #ogImageInput
                                        />
                                        <div class="cursor-pointer" (click)="ogImageInput.click()">
                                            <i class="pi pi-cloud-upload text-3xl text-gray-400 mb-2"></i>
                                            <p class="text-sm text-gray-600">Click to upload OG Image</p>
                                            <p class="text-xs text-gray-400 mt-1">Max 5MB</p>
                                        </div>
                                    } @else {
                                        <i class="pi pi-lock text-2xl text-gray-300 mb-2"></i>
                                        <p class="text-sm text-gray-400">Save page first to upload images</p>
                                    }
                                </div>
                            } @else {
                                <div class="relative group">
                                    <img
                                        [alt]="ogImageFileName()"
                                        [src]="smallImageUrl + ogImageFileName()"
                                        class="w-full h-40 object-cover rounded-lg"
                                    />
                                    <div
                                        class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2"
                                    >
                                        <p-button
                                            icon="pi pi-eye"
                                            severity="secondary"
                                            size="small"
                                            [outlined]="true"
                                            (click)="openImagePreview(smallImageUrl + ogImageFileName())"
                                        ></p-button>
                                        <p-button
                                            icon="pi pi-trash"
                                            severity="danger"
                                            size="small"
                                            [outlined]="true"
                                            (click)="deleteOgImage()"
                                            [loading]="deletingOgImage()"
                                        ></p-button>
                                    </div>
                                    <p class="text-xs text-gray-500 mt-2 truncate">{{ ogImageFileName() }}</p>
                                </div>
                            }
                        </div>
                    </div>

                    <!-- Search Background -->
                    <div>
                        <label class="block mb-3 text-sm font-medium text-gray-700">Search Background (Max 5MB, Min 1700x540)</label>
                        <div class="border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                            @if (!searchBgImageFileName()) {
                                <div class="text-center">
                                    @if (isEditMode) {
                                        <input
                                            type="file"
                                            accept="image/*"
                                            (change)="uploadSearchBgImage($event)"
                                            [disabled]="uploadingSearchBgImage()"
                                            hidden
                                            #searchBgImageInput
                                        />
                                        <div class="cursor-pointer" (click)="searchBgImageInput.click()">
                                            <i class="pi pi-cloud-upload text-3xl text-gray-400 mb-2"></i>
                                            <p class="text-sm text-gray-600">Click to upload Search BG</p>
                                            <p class="text-xs text-gray-400 mt-1">Min 1700x540px</p>
                                        </div>
                                    } @else {
                                        <i class="pi pi-lock text-2xl text-gray-300 mb-2"></i>
                                        <p class="text-sm text-gray-400">Save page first to upload images</p>
                                    }
                                </div>
                            } @else {
                                <div class="relative group">
                                    <img
                                        [alt]="searchBgImageFileName()"
                                        [src]="smallImageUrl + searchBgImageFileName()"
                                        class="w-full h-40 object-cover rounded-lg"
                                    />
                                    <div
                                        class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2"
                                    >
                                        <p-button
                                            icon="pi pi-eye"
                                            severity="secondary"
                                            size="small"
                                            [outlined]="true"
                                            (click)="openImagePreview(smallImageUrl + searchBgImageFileName())"
                                        ></p-button>
                                        <p-button
                                            icon="pi pi-trash"
                                            severity="danger"
                                            size="small"
                                            [outlined]="true"
                                            (click)="deleteSearchBgImage()"
                                            [loading]="deletingSearchBgImage()"
                                        ></p-button>
                                    </div>
                                    <p class="text-xs text-gray-500 mt-2 truncate">{{ searchBgImageFileName() }}</p>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </p-card>

            <!-- Gallery Card -->
            <p-card styleClass="shadow-sm">
                <ng-template pTemplate="title">
                    <div class="flex items-center justify-between w-full">
                        <div class="flex items-center gap-2">
                            <i class="pi pi-images text-green-500"></i>
                            <span>Media Gallery</span>
                        </div>
                        <span class="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{{ gallery().length }} items</span>
                    </div>
                </ng-template>
                <ng-template pTemplate="subtitle">Upload images and YouTube videos</ng-template>

                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <!-- Upload Section -->
                    <div class="lg:col-span-1 space-y-4">
                        <!-- Image Upload -->
                        <div class="border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-blue-400 transition-colors">
                            <p-fileupload
                                #fileUploader
                                (onBeforeUpload)="onBeforeUpload($event)"
                                name="file"
                                [url]="apiUrl + '/cms/contentPages/images'"
                                (onUpload)="onUpload($event)"
                                [multiple]="false"
                                accept="image/*"
                                mode="advanced"
                                maxFileSize="10485760"
                                [showUploadButton]="false"
                                [showCancelButton]="false"
                            >
                                <ng-template #header></ng-template>
                                <ng-template #empty>
                                    <div class="text-center cursor-pointer" (click)="triggerFileSelect()">
                                        <i class="pi pi-image text-3xl text-blue-400 mb-2"></i>
                                        <p class="text-sm font-medium text-gray-700">Drop image here</p>
                                        <p class="text-xs text-gray-400 mt-1">or click to browse</p>
                                    </div>
                                </ng-template>
                            </p-fileupload>
                            <p-button
                                styleClass="w-full mt-3"
                                size="small"
                                [loading]="uploading()"
                                label="Upload Image"
                                icon="pi pi-upload"
                                (click)="onUploadClick()"
                            ></p-button>
                        </div>

                        <!-- YouTube Upload -->
                        <div class="bg-gray-50 rounded-xl p-4">
                            <label class="block mb-2 text-sm font-medium text-gray-700">
                                <i class="pi pi-youtube text-red-500 mr-1"></i>
                                Add YouTube Video
                            </label>
                            <input
                                type="text"
                                pInputText
                                [(ngModel)]="videoUrl"
                                [ngModelOptions]="{ standalone: true }"
                                class="w-full mb-2"
                                placeholder="Paste YouTube URL"
                            />
                            <p-button
                                styleClass="w-full"
                                size="small"
                                label="Add Video"
                                icon="pi pi-plus"
                                (click)="addYoutubeVideo()"
                            ></p-button>
                        </div>
                    </div>

                    <!-- Gallery Grid -->
                    <div class="lg:col-span-2">
                        @if (gallery().length > 0) {
                            <div
                                class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
                                cdkDropList
                                (cdkDropListDropped)="onGalleryDrop($event)"
                            >
                                @for (item of gallery(); track $index) {
                                    <div cdkDrag class="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-move">
                                        @if (item.type === 'IMAGE') {
                                            <img [src]="smallImageUrl + item.name" alt="Gallery Image" class="w-full h-full object-cover" />
                                        } @else if (item.type === 'YOUTUBE') {
                                            <div class="w-full h-full flex items-center justify-center bg-red-50">
                                                <i class="pi pi-youtube text-4xl text-red-500"></i>
                                            </div>
                                        }

                                        <!-- Hover Actions -->
                                        <div
                                            class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2"
                                        >
                                            @if (item.type === 'IMAGE') {
                                                <p-button
                                                    icon="pi pi-eye"
                                                    severity="secondary"
                                                    size="small"
                                                    [outlined]="true"
                                                    (click)="openImagePreview(smallImageUrl + item.name)"
                                                ></p-button>
                                            }
                                            <p-button
                                                icon="pi pi-trash"
                                                severity="danger"
                                                size="small"
                                                [outlined]="true"
                                                (click)="removeGalleryItem(item)"
                                                [loading]="isDeletingImage()"
                                            ></p-button>
                                        </div>

                                        <!-- Drag Handle -->
                                        <div class="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <i class="pi pi-bars text-white/80"></i>
                                        </div>

                                        <!-- File Name Tooltip -->
                                        <div class="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1">
                                            <p class="text-xs text-white truncate">
                                                {{ item.name | slice: 0 : 20 }}{{ item.name.length > 20 ? '...' : '' }}
                                            </p>
                                        </div>
                                    </div>
                                }
                            </div>
                        } @else {
                            <div
                                class="h-full flex flex-col items-center justify-center text-gray-400 min-h-[200px] border-2 border-dashed border-gray-200 rounded-xl"
                            >
                                <i class="pi pi-images text-5xl mb-3"></i>
                                <p class="text-sm">No media items yet</p>
                                <p class="text-xs mt-1">Upload images or add YouTube videos</p>
                            </div>
                        }
                    </div>
                </div>
            </p-card>

            <!-- Language Content Card -->
            <p-card styleClass="shadow-sm">
                <ng-template pTemplate="title">
                    <div class="flex items-center gap-2">
                        <i class="pi pi-language text-orange-500"></i>
                        <span>Content by Language</span>
                    </div>
                </ng-template>
                <ng-template pTemplate="subtitle">Manage page content for each language</ng-template>

                <div class="flex flex-col lg:flex-row gap-6">
                    <!-- Language Sidebar -->
                    <div class="lg:w-64 flex-shrink-0">
                        <div class="space-y-2">
                            @for (tab of languages(); track $index) {
                                <button
                                    type="button"
                                    (click)="activeTabIndex.set($index)"
                                    class="w-full flex items-center justify-between p-3 rounded-lg transition-all text-left"
                                    [class.bg-blue-50]="activeTabIndex() === $index"
                                    [class.border-blue-200]="activeTabIndex() === $index"
                                    [class.border-2]="activeTabIndex() === $index"
                                    [class.border-transparent]="activeTabIndex() !== $index"
                                    [class.hover:bg-gray-50]="activeTabIndex() !== $index"
                                >
                                    <div class="flex items-center gap-3">
                                        <span class="font-medium" [class.text-blue-700]="activeTabIndex() === $index">{{
                                            tab.locale | uppercase
                                        }}</span>
                                        <span
                                            class="text-xs px-2 py-0.5 rounded-full font-medium"
                                            [class.bg-green-100]="tab.status"
                                            [class.text-green-700]="tab.status"
                                            [class.bg-yellow-100]="!tab.status"
                                            [class.text-yellow-700]="!tab.status"
                                        >
                                            {{ tab.status ? 'Live' : 'Draft' }}
                                        </span>
                                    </div>
                                    @if (activeTabIndex() === $index) {
                                        <i class="pi pi-chevron-right text-blue-500 text-sm"></i>
                                    }
                                </button>
                            }
                        </div>
                    </div>

                    <!-- Content Area -->
                    <div class="flex-1 min-w-0">
                        @for (tab of languages(); track $index) {
                            @if (activeTabIndex() === $index) {
                                <div [formGroup]="getFormGroupAt(tab.locale)" class="space-y-6">
                                    <!-- Publish Toggle -->
                                    <div class="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                        <div class="flex items-center gap-3">
                                            <div
                                                class="w-10 h-10 rounded-full flex items-center justify-center"
                                                [class.bg-green-100]="tab.status"
                                                [class.text-green-600]="tab.status"
                                                [class.bg-yellow-100]="!tab.status"
                                                [class.text-yellow-600]="!tab.status"
                                            >
                                                <i class="pi" [class.pi-check-circle]="tab.status" [class.pi-pencil]="!tab.status"></i>
                                            </div>
                                            <div>
                                                <p class="font-medium text-gray-900">{{ tab.status ? 'Published' : 'Draft' }}</p>
                                                <p class="text-xs text-gray-500">
                                                    {{ tab.status ? 'This content is live' : 'Save as draft to edit later' }}
                                                </p>
                                            </div>
                                        </div>
                                        <p-toggleswitch
                                            [ngModel]="tab.status"
                                            (onChange)="togglePublishStatus($index, $event.checked)"
                                            [ngModelOptions]="{ standalone: true }"
                                        ></p-toggleswitch>
                                    </div>

                                    <!-- URL Slug -->
                                    <div>
                                        <label for="url-{{ $index }}" class="block mb-2 text-sm font-medium text-gray-700">
                                            URL Slug @if (tab.status) {<span>*</span>}
                                            @if (formValues().localizedPageContents[$index]?.url && getAbsoluteUrlForLocale(tab.locale)) {
                                                <a
                                                    target="_blank"
                                                    [href]="getAbsoluteUrlForLocale(tab.locale)"
                                                    class="ml-2 text-blue-500 hover:text-blue-700 text-xs"
                                                >
                                                    <i class="pi pi-external-link"></i> Preview
                                                </a>
                                            }
                                        </label>
                                        <input
                                            id="url-{{ $index }}"
                                            type="text"
                                            pInputText
                                            formControlName="url"
                                            class="w-full"
                                            placeholder="e.g., flight-ticket"
                                            [class.ng-invalid]="
                                                getFormGroupAt(tab.locale).get('url')?.invalid &&
                                                getFormGroupAt(tab.locale).get('url')?.touched
                                            "
                                            [class.ng-dirty]="
                                                getFormGroupAt(tab.locale).get('url')?.invalid &&
                                                getFormGroupAt(tab.locale).get('url')?.touched
                                            "
                                        />
                                        @if (
                                            getFormGroupAt(tab.locale).get('url')?.invalid && getFormGroupAt(tab.locale).get('url')?.touched
                                        ) {
                                            <small class="text-red-500 text-xs mt-1 block">URL is required</small>
                                        }
                                    </div>

                                    <!-- Page Title -->
                                    <div>
                                        <label for="title-{{ $index }}" class="block mb-2 text-sm font-medium text-gray-700"
                                            >Page Title (H1) @if (tab.status) {<span>*</span>}</label
                                        >
                                        <input
                                            id="title-{{ $index }}"
                                            type="text"
                                            pInputText
                                            formControlName="title"
                                            class="w-full"
                                            placeholder="Enter page title"
                                            [class.ng-invalid]="
                                                getFormGroupAt(tab.locale).get('title')?.invalid &&
                                                getFormGroupAt(tab.locale).get('title')?.touched
                                            "
                                            [class.ng-dirty]="
                                                getFormGroupAt(tab.locale).get('title')?.invalid &&
                                                getFormGroupAt(tab.locale).get('title')?.touched
                                            "
                                        />
                                        <div class="flex justify-between mt-1">
                                            @if (
                                                getFormGroupAt(tab.locale).get('title')?.invalid &&
                                                getFormGroupAt(tab.locale).get('title')?.touched
                                            ) {
                                                <small class="text-red-500 text-xs">Page title is required</small>
                                            } @else {
                                                <span></span>
                                            }
                                            <small class="text-gray-400 text-xs"
                                                >{{ formValues().localizedPageContents[$index]?.title?.length || 0 }}/60</small
                                            >
                                        </div>
                                    </div>

                                    <!-- Page Content -->
                                    <div>
                                        <label for="content-{{ $index }}" class="block mb-2 text-sm font-medium text-gray-700"
                                            >Page Content *</label
                                        >
                                        <app-rich-text-editor
                                            formControlName="content"
                                            [placeholder]="'Enter page content...'"
                                            [height]="'350px'"
                                        ></app-rich-text-editor>
                                        @if (
                                            getFormGroupAt(tab.locale).get('content')?.invalid &&
                                            getFormGroupAt(tab.locale).get('content')?.touched
                                        ) {
                                            <small class="text-red-500 text-xs mt-1 block">Page content is required</small>
                                        }
                                    </div>

                                    <!-- Info Boxes -->
                                    <div class="border-t border-gray-200 pt-6">
                                        <div class="flex items-center justify-between mb-4">
                                            <div class="flex items-center gap-2">
                                                <i class="pi pi-th-large text-blue-500"></i>
                                                <span class="font-medium text-gray-900">Info Boxes</span>
                                                <span class="text-xs text-gray-400"
                                                    >({{ formValues().localizedPageContents[$index]?.featureBlocks?.length || 0 }})</span
                                                >
                                            </div>
                                            <p-button
                                                label="Add Info Box"
                                                icon="pi pi-plus"
                                                size="small"
                                                (click)="openAddInfoBoxDialog(tab.locale)"
                                            ></p-button>
                                        </div>

                                        @if ((formValues().localizedPageContents[$index]?.featureBlocks?.length || 0) > 0) {
                                            <div
                                                class="grid grid-cols-1 md:grid-cols-2 gap-3"
                                                cdkDropList
                                                (cdkDropListDropped)="onFeatureBlockDrop($event, tab.locale)"
                                            >
                                                @for (block of formValues().localizedPageContents[$index].featureBlocks; track $index) {
                                                    <div
                                                        cdkDrag
                                                        class="bg-gray-50 rounded-lg p-4 flex items-start gap-3 cursor-move hover:bg-gray-100 transition-colors group"
                                                    >
                                                        <i class="pi pi-bars text-gray-400 mt-1"></i>
                                                        <div class="flex-1 min-w-0">
                                                            <div class="flex items-center gap-2">
                                                                <span class="material-icons text-blue-600">{{ block.iconName }}</span>
                                                                <span class="font-medium text-gray-900 truncate">{{ block.title }}</span>
                                                            </div>
                                                            <p class="text-sm text-gray-500 mt-1 line-clamp-2">{{ block.description }}</p>
                                                        </div>
                                                        <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <p-button
                                                                icon="pi pi-pencil"
                                                                severity="secondary"
                                                                size="small"
                                                                [text]="true"
                                                                (click)="openEditInfoBoxDialog(tab.locale, $index)"
                                                            ></p-button>
                                                            <p-button
                                                                icon="pi pi-trash"
                                                                severity="danger"
                                                                size="small"
                                                                [text]="true"
                                                                (click)="removeFeatureBlock(tab.locale, $index)"
                                                            ></p-button>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        } @else {
                                            <div class="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                                <i class="pi pi-plus-circle text-3xl text-gray-300 mb-2"></i>
                                                <p class="text-sm text-gray-500">No info boxes yet</p>
                                                <p class="text-xs text-gray-400 mt-1">Add feature blocks to highlight key information</p>
                                            </div>
                                        }
                                    </div>

                                    <!-- SEO Settings -->
                                    <div class="border-t border-gray-200 pt-6">
                                        <div class="flex items-center gap-2 mb-4">
                                            <i class="pi pi-search text-green-500"></i>
                                            <span class="font-medium text-gray-900">SEO Settings</span>
                                        </div>

                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <!-- Meta Title -->
                                            <div class="md:col-span-2">
                                                <label for="metaTitle-{{ $index }}" class="block mb-2 text-sm font-medium text-gray-700"
                                                    >Meta Title</label
                                                >
                                                <input
                                                    id="metaTitle-{{ $index }}"
                                                    type="text"
                                                    pInputText
                                                    formControlName="metaTitle"
                                                    class="w-full"
                                                    placeholder="SEO title (50-60 chars recommended)"
                                                    [class.ng-invalid]="
                                                        getFormGroupAt(tab.locale).get('metaTitle')?.invalid &&
                                                        getFormGroupAt(tab.locale).get('metaTitle')?.touched
                                                    "
                                                    [class.ng-dirty]="
                                                        getFormGroupAt(tab.locale).get('metaTitle')?.invalid &&
                                                        getFormGroupAt(tab.locale).get('metaTitle')?.touched
                                                    "
                                                />
                                                <div class="flex justify-between mt-1">
                                                    @if (
                                                        getFormGroupAt(tab.locale).get('metaTitle')?.invalid &&
                                                        getFormGroupAt(tab.locale).get('metaTitle')?.touched
                                                    ) {
                                                        <small class="text-red-500 text-xs">Must be less than 60 characters</small>
                                                    } @else {
                                                        <span></span>
                                                    }
                                                    <small class="text-gray-400 text-xs"
                                                        >{{ formValues().localizedPageContents[$index]?.metaTitle?.length || 0 }}/60</small
                                                    >
                                                </div>
                                            </div>

                                            <!-- Meta Description -->
                                            <div class="md:col-span-2">
                                                <label
                                                    for="metaDescription-{{ $index }}"
                                                    class="block mb-2 text-sm font-medium text-gray-700"
                                                    >Meta Description</label
                                                >
                                                <textarea
                                                    id="metaDescription-{{ $index }}"
                                                    rows="3"
                                                    formControlName="metaDescription"
                                                    class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                    placeholder="Brief description for search engines (150-160 chars recommended)"
                                                    [class.ng-invalid]="
                                                        getFormGroupAt(tab.locale).get('metaDescription')?.invalid &&
                                                        getFormGroupAt(tab.locale).get('metaDescription')?.touched
                                                    "
                                                    [class.ng-dirty]="
                                                        getFormGroupAt(tab.locale).get('metaDescription')?.invalid &&
                                                        getFormGroupAt(tab.locale).get('metaDescription')?.touched
                                                    "
                                                    [class.border-red-500]="
                                                        getFormGroupAt(tab.locale).get('metaDescription')?.invalid &&
                                                        getFormGroupAt(tab.locale).get('metaDescription')?.touched
                                                    "
                                                ></textarea>
                                                <div class="flex justify-between mt-1">
                                                    @if (
                                                        getFormGroupAt(tab.locale).get('metaDescription')?.invalid &&
                                                        getFormGroupAt(tab.locale).get('metaDescription')?.touched
                                                    ) {
                                                        <small class="text-red-500 text-xs">Must be less than 160 characters</small>
                                                    } @else {
                                                        <span></span>
                                                    }
                                                    <small class="text-gray-400 text-xs"
                                                        >{{
                                                            getFormGroupAt(tab.locale).get('metaDescription')?.value?.length || 0
                                                        }}/160</small
                                                    >
                                                </div>
                                            </div>

                                            <!-- Keywords -->
                                            <div class="md:col-span-2">
                                                <label for="keywords-{{ $index }}" class="block mb-2 text-sm font-medium text-gray-700"
                                                    >Keywords</label
                                                >
                                                <input
                                                    id="keywords-{{ $index }}"
                                                    type="text"
                                                    pInputText
                                                    formControlName="keywords"
                                                    class="w-full"
                                                    placeholder="e.g., flight, ticket, travel, booking"
                                                />
                                                <small class="text-gray-400 text-xs mt-1 block">Separate keywords with commas</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        }
                    </div>
                </div>
            </p-card>
        </form>
    </div>
</div>
